"use client"

import { useMemo, useState } from "react"
import { ChevronDown, Loader2, Search, SlidersHorizontal } from "lucide-react"

import { TextAction } from "@/components/graphite/text-action"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { PageHeader } from "@/components/graphite/page-header"
import { RecordTabs } from "@/components/graphite/record-tabs"
import { cn } from "@/lib/utils"
import { TonePill } from "@/components/graphite/surface"
import { TINT, avatarHex, stageHex, stageTint } from "@/lib/stage-colors"

/**
 * Candidates, from canvas artboards 5a (wide) and 8g (narrow).
 *
 * The canvas subtitle states the scope: "Everyone in your database, across
 * every job" — so this is the candidate record list, not a pipeline. The table
 * is a surface on the canvas, hairline-ruled with a tinted header row and
 * zebra, and the page header above it is pinned chrome that the body scrolls
 * under.
 *
 * Type follows the house rule: Inter carries language, JetBrains Mono carries
 * machine values. Years of experience is a machine value, so it is mono; every
 * other cell is language.
 *
 * Responsiveness is by *container*, not viewport — this page lives inside the
 * resizable ATS panel, so 8g is not "the phone layout", it is what this page
 * looks like when it is narrow, whether that is a phone or a desktop with the
 * chat pane dragged wide.
 */

export type CandidateRow = {
  id: string
  name: string
  initials: string
  /** "Staff Engineer · Klarna" — role and current company, already joined. */
  role: string
  location: string
  /** Machine value, e.g. "9y". */
  experience: string
  /** Absent when the candidate is not on any job's pipeline. */
  stage?: string
  comments: number
  /** Relative time, e.g. "2 days ago". */
  added: string
}

const TABS = ["All", "Active", "Placed", "Archived"] as const
type Tab = (typeof TABS)[number]

/** 5a's grid, shared by the header row and every body row. */
const GRID = [
  "grid items-center gap-4 px-[18px]",
  // Between 672 and 768 there is not room for all six columns, so Location and
  // Exp drop out — the same move 12b makes with Cover on the dashboard.
  //
  // The fixed columns are narrower than 5a draws them (130/96/150/130). Those
  // widths do not survive contact with the panel: they leave the two flexible
  // columns about 80px each in the 778px ATS column the artboard is itself
  // drawn at, which truncates "Maya Aslan" and "Staff Engineer · Klarna" —
  // neither of which is truncated in the artboard. The canvas is internally
  // inconsistent here, so these are sized to their real content instead.
  "@2xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1.4fr)_132px_104px]",
  "@3xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1.4fr)_112px_56px_132px_104px]",
].join(" ")

function StagePill({ stage, compact }: { stage?: string; compact?: boolean }) {
  if (!stage) {
    // No job–candidate link, so no stage. An em dash is honest; a default
    // "longlist" pill would be an invented fact.
    return <span className="text-[11.5px] text-muted-foreground">—</span>
  }

  return (
    <TonePill hex={stageHex(stage)} compact={compact}>
      {stage}
    </TonePill>
  )
}

function Avatar({
  initials,
  seed,
  size,
}: {
  initials: string
  seed: string
  size: 30 | 36
}) {
  const hex = avatarHex(seed)

  return (
    <div
      className={cn(
        "flex flex-none items-center justify-center rounded-full font-medium",
        size === 30 ? "size-[30px] text-[10px] opacity-85" : "size-9 text-[13px]",
      )}
      style={{ color: hex, backgroundColor: stageTint(hex, TINT.pill) }}
    >
      {initials}
    </div>
  )
}

/** One of 5a's filter pills. Inert options are not offered. */

/** One filter as a row of words, the way the stage rails read elsewhere. */
function SheetFilter({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string
  value: string
  options: string[]
  onChange: (next: string) => void
  className?: string
}) {
  return (
    <div className={className}>
      <div className="text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2.5">
        {["all", ...options].map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={cn(
              "border-b pb-1 text-[12.5px] transition-colors",
              value === option
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {option === "all" ? `All ${label.toLowerCase()}s` : option}
          </button>
        ))}
      </div>
    </div>
  )
}

function FilterPill({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  onChange: (next: string) => void
}) {
  return (
    <span className="relative inline-flex items-center gap-[7px] rounded-md border bg-card py-2 pl-3 pr-[30px] text-[12px] text-muted-foreground">
      {value === "all" ? label : value}
      <ChevronDown className="pointer-events-none absolute right-2.5 size-3.5" />
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        <option value="all">{label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </span>
  )
}

export function GraphiteCandidates({
  rows,
  total,
  isLoading,
  search,
  onSearchChange,
  onOpenCandidate,
  addCandidate,
}: {
  rows: CandidateRow[]
  /** Everyone in the database, before client-side filtering. */
  total: number
  isLoading: boolean
  search: string
  onSearchChange: (next: string) => void
  onOpenCandidate: (id: string) => void
  /** The existing Add Candidate dialog, trigger included. */
  addCandidate?: React.ReactNode
}) {
  const [tab, setTab] = useState<Tab>("All")
  const [stage, setStage] = useState("all")
  const [location, setLocation] = useState("all")
  const [filtersOpen, setFiltersOpen] = useState(false)

  const stages = useMemo(
    () => [...new Set(rows.map((row) => row.stage).filter(Boolean))] as string[],
    [rows],
  )
  const locations = useMemo(
    () => [...new Set(rows.map((row) => row.location).filter(Boolean))].sort(),
    [rows],
  )

  const shown = useMemo(
    () =>
      rows.filter((row) => {
        // The tabs have no status field behind them, so they read the stage:
        // placed is hired, archived is rejected, active is everything still
        // moving. A candidate with no pipeline link counts as active.
        if (tab === "Placed" && row.stage !== "hired") return false
        if (tab === "Archived" && row.stage !== "rejected") return false
        if (tab === "Active" && (row.stage === "hired" || row.stage === "rejected"))
          return false
        if (stage !== "all" && row.stage !== stage) return false
        if (location !== "all" && row.location !== location) return false
        return true
      }),
    [rows, tab, stage, location],
  )

  /*
    Export writes what is on screen, filters included — the button sits beside
    the filter row, so exporting anything else would be a surprise. Everything
    it needs is already in memory, so there is no endpoint behind it.
  */
  const exportCsv = () => {
    const header = ["Name", "Current role", "Location", "Experience", "Stage", "Added"]
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`
    const csv = [
      header.map(escape).join(","),
      ...shown.map((row) =>
        [row.name, row.role, row.location, row.experience, row.stage ?? "", row.added]
          .map(escape)
          .join(","),
      ),
    ].join("\n")

    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }))
    const link = document.createElement("a")
    link.href = url
    link.download = "candidates.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="bottom" className="gap-0 rounded-t-2xl p-0">
          <div className="border-b px-[18px] pb-3.5 pt-4">
            <SheetTitle className="text-[14.5px] font-semibold">Filters</SheetTitle>
          </div>
          <div className="px-[18px] py-5">
            <SheetFilter
              label="Stage"
              value={stage}
              options={stages}
              onChange={setStage}
            />
            <SheetFilter
              label="Location"
              value={location}
              options={locations}
              onChange={setLocation}
              className="mt-5"
            />
            <div className="mt-6 flex items-center gap-[22px] border-t pt-4">
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="text-[12.5px] font-medium text-brand-ink"
              >
                Show {shown.length} candidate{shown.length === 1 ? "" : "s"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStage("all")
                  setLocation("all")
                }}
                className="text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
              >
                Clear
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Pinned chrome — the body scrolls underneath it. 8g labels the count in
          mono on narrow; 5a writes the scope in language on wide. */}
      <PageHeader
        title="Candidates"
        description="Everyone in your database, across every job"
        /* Not "0 records" while the first request is still out — see the
           receipt below. */
        meta={isLoading ? undefined : `${total} records`}
        actions={
          <>
          {/*
            8g gives narrow a single 44px tap target instead of the buttons. It
            had no handler, so stage and location filtering existed only on a
            desktop; the same two now open in a sheet.
          */}
          <button
            type="button"
            aria-label="Filters"
            onClick={() => setFiltersOpen(true)}
            className={cn(
              "ml-auto flex size-11 flex-none items-center justify-center transition-colors @2xl:hidden",
              stage !== "all" || location !== "all"
                ? "text-brand-ink"
                : "text-muted-foreground",
            )}
          >
            <SlidersHorizontal className="size-[18px]" />
          </button>

          {/*
            One pill and one word. Canvas turn 33 gives a page's single create a
            filled action and leaves everything beside it quiet, so adding a
            candidate is the pill and Export CSV is the grey word. An earlier
            note here called a filled pill pre-Graphite; turn 33 retired that,
            and `PageAction` is where the three tones now live.

            Adding someone is the reason most people open this page, and it used
            to sit inside a group gated at @2xl — so in the ATS pane and on a
            phone the only way to reach it was to type the query string the
            dialog opens on. Export CSV is the one that can wait for a wide
            window, so it is the one that is gated.

            `addCandidate` is a dialog, not a button: it holds the form's state
            and portals its content, and it opens itself from the URL. Mounted
            twice — once per width — `?newCandidate=true` raised two of them,
            stacked, each with its own copy of the form. So it is mounted once
            and the widths move it instead.
          */}
          <div className="flex items-center gap-[18px] @2xl:ml-auto @2xl:gap-[22px]">
            <TextAction
              onClick={exportCsv}
              className="hidden text-[12.5px] @2xl:inline-flex"
            >
              Export CSV
            </TextAction>
            {addCandidate}
          </div>
          </>
        }
      >
        <RecordTabs tabs={TABS} value={tab} onChange={setTab} />
      </PageHeader>

      {/*
        Canvas. This used to collapse below the wide breakpoint on the reasoning
        that a phone screen is itself the surface — but a list running edge to
        edge on a flat ground reads as an unfinished page here, and the two
        other list pages carry their card at every width. Consistency across
        them beats the platform convention.
      */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4 pt-3.5 @2xl:gap-3.5 @2xl:px-[26px] @2xl:pb-[26px] @2xl:pt-[18px]">
        <div className="flex flex-none flex-wrap items-center gap-2 @2xl:gap-2">
          <span className="relative inline-flex min-w-0 flex-1 items-center gap-2 rounded-md border bg-card px-3 py-[9px] @2xl:mr-1 @2xl:w-[248px] @2xl:flex-none">
            <Search className="size-3.5 flex-none text-muted-foreground" />
            <input
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search by name"
              aria-label="Search candidates by name"
              className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
            />
          </span>

          {/* 5a draws four pills: stages, jobs, owners, location. Stage and
              location are derivable from what is loaded; job and owner are not
              on the candidate contract, so those two are left out rather than
              drawn dead. */}
          <div className="hidden items-center gap-2 @2xl:flex">
            <FilterPill
              label="All stages"
              value={stage}
              options={stages}
              onChange={setStage}
            />
            <FilterPill
              label="Location"
              value={location}
              options={locations}
              onChange={setLocation}
            />
          </div>

          {/*
            What the filters left. Gated at @2xl, the one width where most of a
            list is off-screen was the width that would not say how long it is —
            and then, once it was always on screen, it said "0 candidates ·
            0 shown" beside the words "Loading candidates…". A count of an
            unread list is not a small count, it is a wrong one.
          */}
          {!isLoading && (
            <span className="w-full whitespace-nowrap text-[11.5px] text-muted-foreground @2xl:ml-auto @2xl:w-auto">
              {total} candidates · {shown.length} shown
            </span>
          )}
        </div>

        {/*
          The table is a surface, so it carries one. Only the header row had
          `bg-card`, and the body was letting the page's grey through — which is
          invisible in dark, where the page and a card are almost the same
          colour, and obvious in light, where it left a white header sitting on
          a grey table.
        */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border bg-card">
          {/* Wide: 5a's six-column table. */}
          <div
            className={cn(
              GRID,
              "hidden shrink-0 border-b bg-card py-2.5 text-[11.5px] font-medium text-muted-foreground @2xl:grid",
            )}
          >
            <div>Candidate</div>
            <div>Current role</div>
            <div className="hidden @3xl:block">Location</div>
            <div className="hidden @3xl:block">Exp</div>
            <div>Stage</div>
            {/* 5a says "Updated"; the contract only carries when a candidate was
                added, so the column says what it shows. */}
            <div className="text-right">Added</div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex items-center gap-2 p-4 text-[12.5px] text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                Loading candidates…
              </div>
            )}

            {!isLoading && shown.length === 0 && (
              <div className="p-6 text-[12.5px] text-muted-foreground">
                No candidates match these filters.
              </div>
            )}

            {shown.map((row, index) => (
              <div key={row.id}>
                {/* Wide row. */}
                <button
                  type="button"
                  onClick={() => onOpenCandidate(row.id)}
                  className={cn(
                    GRID,
                    "hidden h-14 w-full border-b text-left transition-colors hover:bg-accent @2xl:grid",
                    // Zebra: inside a surface, only header rows and zebra may
                    // be tinted. A tint off the foreground keeps that true in
                    // both themes rather than hardcoding 5a's dark value.
                    index % 2 === 1 && "bg-foreground/[0.02]",
                  )}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar initials={row.initials} seed={row.id} size={30} />
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium">{row.name}</div>
                      {row.comments > 0 && (
                        <div className="mt-[3px] text-[10.5px] leading-[1.3] text-muted-foreground">
                          {row.comments} comment{row.comments === 1 ? "" : "s"}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="truncate text-[12.5px] leading-[1.3] text-muted-foreground">
                    {row.role}
                  </div>
                  <div className="hidden truncate text-[12.5px] text-muted-foreground @3xl:block">
                    {row.location}
                  </div>
                  <div className="hidden font-mono text-[12px] font-medium @3xl:block">
                    {row.experience}
                  </div>
                  <div>
                    <StagePill stage={row.stage} />
                  </div>
                  <div className="text-right text-[11.5px] text-muted-foreground">
                    {row.added}
                  </div>
                </button>

                {/* Narrow row, from 8g. */}
                <button
                  type="button"
                  onClick={() => onOpenCandidate(row.id)}
                  className="flex w-full items-center gap-[11px] border-b px-4 py-3.5 text-left transition-colors hover:bg-accent @2xl:hidden"
                >
                  <Avatar initials={row.initials} seed={row.id} size={36} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-medium leading-[1.2]">
                      {row.name}
                    </div>
                    <div className="mt-[5px] truncate text-[11.5px] leading-[1.2] text-muted-foreground">
                      {row.role}
                    </div>
                  </div>
                  <StagePill stage={row.stage} compact />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
